import type { ReactNode } from "react";
import { Modal } from "antd";
import { Trans } from "react-i18next";
import { CommonButton } from "@components/CommonButton";
import { IconClose } from "@icons/index";
import { IconSolidCircleWarning } from "@kalodata/kds-design-icons";

export interface DangerConfirmModalProps {
  open: boolean;
  title: ReactNode;
  description: ReactNode;
  /** 危险/次要操作按钮文案，例 "Leave" / "Unschedule" / "Delete" */
  confirmText: ReactNode;
  /** 取消按钮文案，默认 <Trans>Cancel</Trans> */
  cancelText?: ReactNode;
  /** 危险按钮 loading 态 */
  confirmLoading?: boolean;
  /** 危险操作（左侧次按钮） */
  onConfirm: () => void;
  /** 取消（右侧高亮按钮 + 关闭按钮 + ESC） */
  onCancel: () => void;
}

export const DangerConfirmModal = ({
  open,
  title,
  description,
  confirmText,
  cancelText,
  confirmLoading,
  onConfirm,
  onCancel,
}: DangerConfirmModalProps) => {
  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={440}
      centered
      destroyOnHidden
      mask={{ closable: false }}
      closable={false}
      styles={{ container: { padding: 0 }, body: { padding: 0 } }}
    >
      <div className="flex flex-col rounded-[12px] border border-kds-border bg-kds-bg">
        <div className="flex h-[64px] items-center gap-[8px] p-[16px]">
          <div className="flex min-w-0 flex-1 items-center gap-[8px]">
            <IconSolidCircleWarning className="size-[20px] shrink-0 text-kds-warning-fg" />
            <p className="m-0 truncate text-[16px] font-semibold leading-[24px] text-kds-fg">{title}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="flex size-[32px] shrink-0 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent text-kds-fg-soft hover:bg-kds-bg-overlay"
          >
            <IconClose className="size-[20px]" />
          </button>
        </div>

        <div className="flex flex-col items-start justify-center px-[16px] pt-0 pb-[16px]">
          <p className="m-0 w-full text-[16px] font-normal leading-[24px] text-kds-fg-soft">{description}</p>
        </div>

        <div className="flex h-[64px] items-center justify-end gap-[8px] border-t border-kds-border p-[16px]">
          <CommonButton type="default" size="middle" loading={confirmLoading} onClick={onConfirm}>
            {confirmText}
          </CommonButton>
          <CommonButton type="primary" size="middle" onClick={onCancel}>
            {cancelText ?? <Trans>Cancel</Trans>}
          </CommonButton>
        </div>
      </div>
    </Modal>
  );
};
